import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import { Typography, Row, Col, Card, Spin, Alert, Skeleton } from 'antd';
import { EnvironmentOutlined, CalendarOutlined, CompassOutlined } from '@ant-design/icons';
import { getCategories } from '../services/categoriesService';
import { getCurrentDestination } from '../services/destinationsService';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

function FeatureCard({ icon, title, description, path }) {
  const navigate = useNavigate();

  return (
    <Card hoverable onClick={() => navigate(path)} style={{ height: '100%', textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', marginBottom: '16px' }}>{icon}</div>
      <Title level={4}>{title}</Title>
      <Text>{description}</Text>
    </Card>
  );
}

function CategoryCard({ category }) {
  const navigate = useNavigate();

  return (
    <Card
      hoverable
      cover={
        <img
          alt={category.name}
          src={category.image_url || 'https://source.unsplash.com/random?category'}
          style={{ height: 140, objectFit: 'cover' }}
        />
      }
      onClick={() => navigate(`/places?category=${category.id}`)}
    >
      <Card.Meta title={category.name} description={category.description} />
    </Card>
  );
}

function Home() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [destinationData, categoriesData] = await Promise.all([
          getCurrentDestination(),
          getCategories(),
        ]);

        setDestination(destinationData);
        setCategories(categoriesData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      {loading ? (
        <Skeleton.Image style={{ width: '100%', height: 600 }} />
      ) : error ? (
        <Alert message={error} type="error" style={{ marginBottom: 24 }} />
      ) : (
        <div
          style={{
            position: 'relative',
            color: '#fff',
            marginBottom: 24,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url(${
              destination?.image_url || 'https://source.unsplash.com/random?destination'
            })`,
            minHeight: '600px',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              left: 0,
              backgroundColor: 'rgba(0,0,0,.5)',
            }}
          />
          <div
            style={{
              position: 'relative',
              padding: '64px 0',
              height: '100%',
            }}
          >
            <div
              style={{
                maxWidth: 800,
                marginTop: '120px',
                padding: '0 24px',
              }}
            >
              <Title
                level={1}
                style={{
                  color: 'white',
                  marginBottom: 16,
                  fontSize: '3.75rem',
                  fontWeight: 'bold',
                }}
              >
                {destination?.name}, {destination?.state}
              </Title>
              <Title
                level={3}
                style={{
                  color: 'white',
                  fontWeight: 'normal',
                  marginTop: 0,
                }}
              >
                {destination?.description ||
                  'Discover hidden gems, local events, and guided tours in your destination.'}
              </Title>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <div style={{ padding: '64px 24px' }}>
        <Row gutter={[32, 32]} justify="center">
          <Col xs={24} sm={8}>
            <FeatureCard
              icon={<EnvironmentOutlined style={{ fontSize: 40 }} />}
              title="Discover Places"
              description="Find interesting locations and hidden gems around the city."
              path="/places"
            />
          </Col>
          <Col xs={24} sm={8}>
            <FeatureCard
              icon={<CalendarOutlined style={{ fontSize: 40 }} />}
              title="Local Events"
              description="Stay updated with the latest events and activities."
              path="/events"
            />
          </Col>
          <Col xs={24} sm={8}>
            <FeatureCard
              icon={<CompassOutlined style={{ fontSize: 40 }} />}
              title="Guided Tours"
              description="Experience the city with curated guided tours."
              path="/tours"
            />
          </Col>
        </Row>
      </div>

      {/* Categories Section */}
      <div style={{ padding: '64px 24px' }}>
        <Title level={2} style={{ marginBottom: 24 }}>
          Categories
        </Title>
        {loading ? (
          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Alert message={error} type="error" style={{ margin: '16px 0' }} />
        ) : (
          <Row gutter={[32, 32]}>
            {categories.map(category => (
              <Col key={category.id} xs={24} sm={12} md={6}>
                <CategoryCard category={category} />
              </Col>
            ))}
          </Row>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Home;
